import { useCallback, useRef } from 'react';
import createClient from '@eid-easy/eideasy-browser-client';
import * as Sentry from '@sentry/react';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import {
	EidEasySignStatus,
	OutstandingReason,
} from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import {
	ensureError,
	isEidEasySandbox,
	isUserInitiatedError,
	toErrorKey,
} from './useEidEasySign.utils';
import { buildOutstandingDocuments, classifyPackageCompletion } from './useBulkDrawSign.utils';
import type { BulkSignSelection } from './useBulkEidEasySign';
import type {
	BulkDrawPackage,
	BulkDrawPrepareResponse,
	DrawPackageOutcome,
	EidEasyBulkCompleteResponse,
	EidEasyBulkPrepareResponse,
	SignDrawPackageInput,
	UseBulkDrawSignOptions,
} from './useBulkDrawSign.types';

/**
 * Bulk simple sign (handwritten-simple-signature).
 *
 * The user draws once. eID Easy offers handwritten signing only on a single-file doc_id, so
 * bulk-prepare returns one package per document; each is signed and completed in turn, and any
 * selected document left unsigned is reported through `onOutstanding` instead of success.
 */
export const useBulkDrawSign = ({
	basePath,
	onStatusChange,
	onError,
	onProgress,
	onComplete,
	onOutstanding,
}: UseBulkDrawSignOptions) => {
	const docIdRef = useRef<string | null>(null);

	const signDrawPackage = useCallback(
		async ({
			drawPackage,
			prepare,
			signatureDataUrl,
		}: SignDrawPackageInput): Promise<DrawPackageOutcome> => {
			const { instanceId, docId } = drawPackage;
			docIdRef.current = docId;

			if (!prepare.mockMode) {
				try {
					const client = createClient({
						clientId: prepare.clientId,
						docId,
						language: 'en',
						sandbox: isEidEasySandbox(prepare.baseUrl),
					});
					await new Promise<void>((resolve, reject) => {
						client.signature.handwrittenSignature.start({
							handwrittenSignatureDataUrl: signatureDataUrl,
							success: () => resolve(),
							fail: (failError: unknown) => reject(ensureError(failError)),
						});
					});
				} catch (err: unknown) {
					Sentry.captureException(err, {
						tags: { signingFlow: 'bulk-draw', phase: 'sign' },
					});
					return { instanceId, signed: false, reason: OutstandingReason.SIGN_FAILED };
				}
			}

			try {
				const completeResult = await makeRequest<EidEasyBulkCompleteResponse>({
					endpoint: `${basePath}/eid-easy/bulk-complete`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({ docId }),
				});
				return classifyPackageCompletion({ instanceId, result: completeResult.payload });
			} catch (err: unknown) {
				const outcome = classifyPackageCompletion({ instanceId, error: err });
				if (!outcome.signed) {
					Sentry.captureException(err, {
						tags: { signingFlow: 'bulk-draw', phase: 'complete' },
					});
				}
				return outcome;
			}
		},
		[basePath]
	);

	const startDraw = useCallback(
		async (
			signatureDataUrl: string,
			source: DrawnSignatureSource,
			selection: BulkSignSelection
		) => {
			onStatusChange(EidEasySignStatus.PREPARING);
			onError('');

			let prepare: BulkDrawPrepareResponse;
			let drawPackages: BulkDrawPackage[];
			try {
				const prepareResult = await makeRequest<EidEasyBulkPrepareResponse>({
					endpoint: `${basePath}/eid-easy/bulk-prepare`,
					method: HTTP_METHOD.POST,
					body: JSON.stringify({
						...selection,
						method: EidEasySigningMethod.DRAW,
						drawnSignatureBase64: signatureDataUrl,
						drawnSignatureSource: source,
					}),
				});
				prepare = prepareResult.payload;
				// A backend without per-document packages signs nothing, which must not read as done.
				if (!prepare.drawPackages) {
					throw new Error('bulk-prepare returned no drawPackages');
				}
				drawPackages = prepare.drawPackages;
			} catch (err: unknown) {
				if (!isUserInitiatedError(err)) {
					Sentry.captureException(err, {
						tags: { signingFlow: 'bulk-draw', phase: 'prepare' },
					});
				}
				onError(toErrorKey(err));
				onStatusChange(EidEasySignStatus.ERROR);
				return;
			}

			const packageOutcomes: DrawPackageOutcome[] = [];
			onStatusChange(EidEasySignStatus.COMPLETING);
			for (const [index, drawPackage] of drawPackages.entries()) {
				onProgress({ phase: 'completing', current: index + 1, total: drawPackages.length });
				packageOutcomes.push(
					await signDrawPackage({ drawPackage, prepare, signatureDataUrl })
				);
			}
			docIdRef.current = null;
			onProgress(null);

			const outstandingDocuments = buildOutstandingDocuments({ prepare, packageOutcomes });
			if (outstandingDocuments.length > 0) {
				onOutstanding(outstandingDocuments);
				return;
			}
			onStatusChange(EidEasySignStatus.DONE);
			onComplete();
		},
		[basePath, onStatusChange, onError, onProgress, onComplete, onOutstanding, signDrawPackage]
	);

	const reset = useCallback(() => {
		docIdRef.current = null;
	}, []);

	return { startDraw, reset };
};
